import { createFileRoute } from '@tanstack/react-router'
import { useCFAuth } from '@/lib/auth/cf-auth-context'
import { hasBillingAccess } from '@/lib/cloudflare/billing-client'
import { SettingsPage } from '@/components/settings-page'
import { Topbar } from '@/components/topbar'

type SettingsSection = 'labels' | 'automation' | 'account'
type LegacySettingsSection = SettingsSection | 'ai-model'

interface SettingsSearch {
  section: LegacySettingsSection
  billingSuccess?: boolean
  billingCanceled?: boolean
  autoStartTrial?: boolean
  billingRequired?: boolean
  plan?: 'pro' | 'business'
  interval?: 'monthly' | 'yearly'
  syncAutoStart?: boolean
}

export const Route = createFileRoute('/settings')({
  component: SettingsRoute,
  validateSearch: (search: Record<string, unknown>): SettingsSearch => ({
    section: (search.section as SettingsSection) || 'labels',
    billingSuccess: search.billingSuccess === true || search.billingSuccess === 'true' ? true : undefined,
    billingCanceled: search.billingCanceled === true || search.billingCanceled === 'true' ? true : undefined,
    autoStartTrial: search.autoStartTrial === true || search.autoStartTrial === 'true' ? true : undefined,
    billingRequired: search.billingRequired === true || search.billingRequired === 'true' ? true : undefined,
    plan: search.plan === 'business' ? 'business' : search.plan === 'pro' ? 'pro' : undefined,
    interval: search.interval === 'monthly' ? 'monthly' : search.interval === 'yearly' ? 'yearly' : undefined,
    syncAutoStart: search.syncAutoStart === true || search.syncAutoStart === 'true' ? true : undefined,
  })
})

function SettingsRoute() {
  const { status, user } = useCFAuth()
  const {
    section,
    billingSuccess,
    billingCanceled,
    autoStartTrial,
    billingRequired,
    plan,
    interval,
  } = Route.useSearch()

  // Redirect to home if not authenticated
  if (status === 'unauthenticated') {
    window.location.href = '/'
    return null
  }

  if (status === 'loading') {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    )
  }

  const requiresBilling = Boolean(billingRequired) || !hasBillingAccess(user?.subscriptionStatus)
  const normalizedSection: SettingsSection = section === 'ai-model' ? 'labels' : section
  const activeSection = requiresBilling ? 'account' : normalizedSection

  return (
    <div className="min-h-screen bg-background">
      <Topbar />
      <main className="container mx-auto pt-16 pb-8 px-4 sm:px-6 lg:px-8">
        <SettingsPage
          activeSection={activeSection}
          billingSuccess={billingSuccess}
          billingCanceled={billingCanceled}
          autoStartTrial={autoStartTrial}
          billingRequired={requiresBilling}
          checkoutPlan={plan}
          checkoutInterval={interval}
        />
      </main>
    </div>
  )
}
